import type { Dispatch, RefObject, SetStateAction } from 'react';
import React, { useCallback, useMemo, useRef } from 'react';
import type { Row } from 'react-table';

import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import type { EntityModel } from '../../../pages/Trafficking/TraffickingPage/modelConverters';
import useVirtualizedRows from '../hooks/useVirtualizedRows';
import TraffickingTableRow from './TraffickingTableRow';

type Props = {
  tbodyRef: RefObject<HTMLTableSectionElement | null>;
  prepareRow: (row: Row<EntityModel>) => void;
  rows: Row<EntityModel>[];
  selectedColIndex: number;
  selectedRowIndex: number;
  lastSelectedRowId: string;
  setLastSelectedRowId: Dispatch<SetStateAction<string>>;
  visibleColumnIdsKey: string;
};

const TraffickingTableRows = ({
  tbodyRef,
  prepareRow,
  rows = [],
  selectedColIndex,
  selectedRowIndex,
  lastSelectedRowId,
  setLastSelectedRowId,
  visibleColumnIdsKey,
}: Props): JSX.Element => {
  const { adsVsmLineItemNavigationLinks } = useFeatureFlags();
  const { visibleRange, topSpacerHeight, bottomSpacerHeight } = useVirtualizedRows(rows.length, tbodyRef);

  const visibleRows = useMemo(() => rows.slice(visibleRange.start, visibleRange.end), [
    rows,
    visibleRange.start,
    visibleRange.end,
  ]);

  visibleRows.forEach((row) => prepareRow(row));

  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const lastSelectedRowIdRef = useRef(lastSelectedRowId);
  lastSelectedRowIdRef.current = lastSelectedRowId;

  const onTableRowClick = useCallback(
    (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>, rowId: string): void => {
      const currentRows = rowsRef.current;
      const row = currentRows.find((r) => r.id === rowId);
      if (!row) {
        return;
      }

      if (e.shiftKey) {
        const anchor = lastSelectedRowIdRef.current;
        const lastSelectedRowIndex = anchor ? currentRows.findIndex((el: Row<EntityModel>) => el.id === anchor) : 0;
        const currentSelectedRowIndex = currentRows.findIndex((el: Row<EntityModel>) => el.id === row.id);
        let rangeForSelection: Row<EntityModel>[] = [];

        if (currentSelectedRowIndex > lastSelectedRowIndex) {
          rangeForSelection = currentRows.slice(lastSelectedRowIndex, currentSelectedRowIndex + 1);
        } else {
          rangeForSelection = currentRows.slice(currentSelectedRowIndex, lastSelectedRowIndex + 1);
        }

        rangeForSelection.forEach((r) => r.toggleRowSelected(true));
        window.getSelection()?.removeAllRanges();
      } else if (adsVsmLineItemNavigationLinks) {
        if (!row.isSelected && e.detail <= 1) {
          row.toggleRowSelected(true);
        }
      } else {
        row.toggleRowSelected();
      }

      setLastSelectedRowId(row.id);
    },
    [setLastSelectedRowId, adsVsmLineItemNavigationLinks]
  );

  return (
    <>
      {topSpacerHeight > 0 && <tr style={{ height: topSpacerHeight }} aria-hidden="true" />}
      {visibleRows.map((row: Row<EntityModel>) => (
        <TraffickingTableRow
          key={row.id}
          row={row}
          visibleColumnIdsKey={visibleColumnIdsKey}
          isRowSelected={row.isSelected === true}
          selectedColIndex={selectedColIndex}
          selectedRowIndex={selectedRowIndex}
          onTableRowClick={onTableRowClick}
        />
      ))}
      {bottomSpacerHeight > 0 && <tr style={{ height: bottomSpacerHeight }} aria-hidden="true" />}
    </>
  );
};

export default TraffickingTableRows;
